import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import type { ReactElement } from "react";
import type { UnitWithWeapons } from "@/lib/utils/unit-weapons-joined";
import {
  isDiff,
  isArrayDiffAdded,
  isArrayDiffRemoved,
  isArrayDiffModified,
  extractDiffValues,
  expandJsonDiffpatchArrayDeltaToChanges,
  type DiffResult,
} from "@/lib/utils/patch-diff";
import { getDivisionImagePath } from "@/lib/utils/division-images";
import type { Division } from "@/lib/types/deck-builder";
import { TraitIcon } from "@/components/ui/trait-icon";
import { shouldOmitField } from "./patch-notes-omit";
import { shouldShowField } from "./field-filter";
import { opticsTierIndexToLabel } from "@/lib/utils/optics-paliers";

interface UnitChangeCardProps {
  unit: UnitWithWeapons;
  oldUnit?: UnitWithWeapons;
  diff: DiffResult;
  changeType: "added" | "removed" | "changed";
  divisionsMap?: Map<string, Division>;
  selectedFields?: Set<string>;
}

/**
 * Get unit image filename from descriptor name
 */
function getUnitImageFilename(descriptorName: string): string {
  const withoutPrefix = descriptorName.replace(/^Descriptor_Unit_/, "");
  return `${withoutPrefix}.png`;
}

/**
 * Format field name for display (convert camelCase to Title Case)
 */
function formatFieldName(fieldName: string): string {
  // Handle nested fields (e.g., "ammo.name" -> "Ammo Name")
  if (fieldName.includes(".")) {
    return fieldName
      .split(".")
      .map((part) => formatFieldName(part))
      .join(" → ");
  }
  
  // Convert camelCase to Title Case
  return fieldName
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * `weapons[0].ammo.heDamage` -> `heDamage`
 * `weapons[0].supplyCost` -> `supplyCost`
 * Also supports dotted index syntax (`weapons.0.*`).
 */
function stripWeaponsPathPrefix(fieldPath: string): string | null {
  const bracketedAmmo = fieldPath.match(/^weapons\[\d+]\.ammo\.(.+)$/i);
  if (bracketedAmmo?.[1]) return bracketedAmmo[1];
  const dottedAmmo = fieldPath.match(/^weapons\.\d+\.ammo\.(.+)$/i);
  if (dottedAmmo?.[1]) return dottedAmmo[1];
  const bracketedGeneric = fieldPath.match(/^weapons\[\d+]\.(.+)$/i);
  if (bracketedGeneric?.[1]) return bracketedGeneric[1];
  const dottedGeneric = fieldPath.match(/^weapons\.\d+\.(.+)$/i);
  if (dottedGeneric?.[1]) return dottedGeneric[1];
  return null;
}

/**
 * Short table label for weapon rows; full path in tooltip on hover.
 */
function patchTableFieldLabel(fieldPath: string): { shortLabel: string; fullLabel: string; hasFullPath: boolean } {
  const fullLabel = formatFieldName(fieldPath).trim() || "—";
  const tail = stripWeaponsPathPrefix(fieldPath);
  if (!tail) {
    return { shortLabel: fullLabel, fullLabel, hasFullPath: false };
  }
  const shortLabel = formatFieldName(tail).trim() || fullLabel;
  if (shortLabel === fullLabel) {
    return { shortLabel, fullLabel, hasFullPath: false };
  }
  return { shortLabel, fullLabel, hasFullPath: true };
}

function FieldLabelCell({ fieldPath }: { fieldPath: string }) {
  const { shortLabel, fullLabel, hasFullPath } = patchTableFieldLabel(fieldPath);
  if (!hasFullPath) {
    return <span className="font-medium text-muted-foreground">{shortLabel}</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="cursor-help border-0 bg-transparent p-0 text-left font-medium text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 underline-offset-2"
        >
          {shortLabel}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[min(28rem,calc(100vw-2rem))] text-xs">
        {fullLabel}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Format value for display
 */
function formatValue(value: any): string {
  if (value === null || value === undefined) {
    return "N/A";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : "None";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

const OPTICS_TIER_DIFF_FIELD_LEAVES = new Set(["optics", "airOptics", "lowAltOptics"]);

function formatPatchNotesFieldValue(fieldPath: string, value: any): string {
  const leaf = fieldPath.includes(".") ? fieldPath.slice(fieldPath.lastIndexOf(".") + 1) : fieldPath;
  if (OPTICS_TIER_DIFF_FIELD_LEAVES.has(leaf) && typeof value === "number") {
    const label = opticsTierIndexToLabel(value);
    if (label !== null) {
      return label;
    }
  }
  return formatValue(value);
}

/** Table wrapper for scalar field diffs — columns stay aligned without horizontal scroll. */
function PatchFieldChangesTable({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full min-w-0">
      <table className="w-full table-fixed border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-border/60 bg-muted/35">
            <th
              scope="col"
              className="w-[46%] px-1.5 py-1.5 align-bottom text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Field
            </th>
            <th
              scope="col"
              className="w-[27%] px-1.5 py-1.5 align-bottom text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Before
            </th>
            <th
              scope="col"
              className="w-[27%] px-1.5 py-1.5 align-bottom text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              After
            </th>
          </tr>
        </thead>
        <tbody className="align-top">{children}</tbody>
      </table>
    </div>
  );
}

/**
 * One table row for old → new (use inside `<PatchFieldChangesTable>`).
 */
function renderFieldChangeRow(
  fieldName: string,
  oldValue: any,
  newValue: any,
  rowKey?: string
) {
  return (
    <tr
      key={rowKey ?? (fieldName || "row")}
      className="border-b border-border/40 last:border-b-0 odd:bg-muted/[0.08]"
    >
      <td className="break-words px-1.5 py-1.5 [overflow-wrap:anywhere]">
        <FieldLabelCell fieldPath={fieldName} />
      </td>
      <td className="break-words px-1.5 py-1.5 text-red-600 [overflow-wrap:anywhere] dark:text-red-400 tabular-nums">
        {formatPatchNotesFieldValue(fieldName, oldValue)}
      </td>
      <td className="break-words px-1.5 py-1.5 text-green-600 [overflow-wrap:anywhere] dark:text-green-400 tabular-nums">
        {formatPatchNotesFieldValue(fieldName, newValue)}
      </td>
    </tr>
  );
}

/**
 * Recursively find all changes in a diff object
 */
function findAllChanges(diff: any, path: string = "", selectedFields?: Set<string>): Array<{ path: string; oldValue: any; newValue: any }> {
  const changes: Array<{ path: string; oldValue: any; newValue: any }> = [];

  if (!diff || typeof diff !== "object") {
    return changes;
  }

  // If it's a direct diff array [oldValue, newValue]
  const directDiff = extractDiffValues(diff);
  if (directDiff) {
    changes.push({ path, oldValue: directDiff.oldValue, newValue: directDiff.newValue });
    return changes;
  }

  // If it's an array diff marker (['+', value], ['-', value], ['~', index, diff])
  if (Array.isArray(diff) && diff.length > 0 && typeof diff[0] === "string") {
    // This is an array diff marker - skip it here, handle in calling code
    return changes;
  }

  // Recursively traverse object
  Object.entries(diff).forEach(([key, value]) => {
    const newPath = path ? `${path}.${key}` : key;
    
    // Skip omitted fields
    if (shouldOmitField(newPath)) {
      return;
    }
    
    
    // Handle array diff format (for divisions, specialities, etc.)
    // Check if this is an array of diff markers like [['+', value], ['-', value]]
    // jsondiffpatch stores array diffs as arrays where each element is a diff marker
    if (Array.isArray(value) && value.length > 0) {
      // Check if first element is a diff marker array (format: ['+', value], ['-', value], etc.)
      const firstElement = value[0];
      if (Array.isArray(firstElement) && firstElement.length >= 2 && typeof firstElement[0] === "string") {
        // This is an array of diff markers - if the field is selected, we have changes
        if (selectedFields && selectedFields.size > 0) {
          // For specialities and divisions, check if the field name itself is selected
          const isSpecialitiesOrDivisions = key === "specialities" || key === "divisions" || newPath === "specialities" || newPath === "divisions";
          if (isSpecialitiesOrDivisions && selectedFields.has(key)) {
            // Direct match on field name
            changes.push({ path: newPath, oldValue: "[Array changes]", newValue: "[Array changes]" });
            return;
          }
          // Direct check: if the field name matches, or if shouldShowField says it matches
          if (selectedFields.has(newPath) || shouldShowField(newPath, selectedFields)) {
            // We have changes in this array field
            changes.push({ path: newPath, oldValue: "[Array changes]", newValue: "[Array changes]" });
          }
        } else {
          // No filtering, so any array changes count
          changes.push({ path: newPath, oldValue: "[Array changes]", newValue: "[Array changes]" });
        }
        return;
      }
    }
    
    // Also handle the case where specialities/divisions is stored as an object with numeric keys
    // e.g., { specialities: { "0": ['+', value], "1": ['-', value] } }
    // In this case, when we encounter a numeric key under "specialities", check if the parent matches
    const isNumericKey = /^\d+$/.test(key);
    const isSimpleStringArrayField = path === "specialities" || path === "divisions";
    // Check if this is a diff marker (format: ['+', value] or ['-', value])
    const isDiffMarker = Array.isArray(value) && value.length >= 2 && typeof value[0] === "string" && (value[0] === '+' || value[0] === '-' || value[0] === '~');
    if (isNumericKey && isSimpleStringArrayField && isDiffMarker) {
      // This is a diff marker array under a simple string array field
      if (selectedFields && selectedFields.size > 0) {
        // Direct check: if the parent field name matches, or if shouldShowField says it matches
        if (selectedFields.has(path) || shouldShowField(path, selectedFields)) {
          // We have changes in this array field
          changes.push({ path: path, oldValue: "[Array changes]", newValue: "[Array changes]" });
        }
      } else {
        changes.push({ path: path, oldValue: "[Array changes]", newValue: "[Array changes]" });
      }
      return;
    }
    
    // Also check if we're at the "specialities" or "divisions" key and the value is an object
    // jsondiffpatch may store array diffs as objects with _t: 'a' marker and numeric keys
    // Format: { specialities: { _t: 'a', 0: ['+', value], 1: ['-', value] } }
    if ((key === "specialities" || key === "divisions") && value && typeof value === "object" && !Array.isArray(value)) {
      // Check if this is a jsondiffpatch array diff (has _t: 'a' marker)
      const isArrayDiff = (value as any)._t === 'a';
      // Or check if it contains numeric keys with diff markers
      const hasDiffMarkers = Object.keys(value).some(k => {
        if (k === '_t') return false; // Skip the _t marker
        const v = (value as any)[k];
        return Array.isArray(v) && v.length >= 2 && typeof v[0] === "string" && (v[0] === '+' || v[0] === '-' || v[0] === '~');
      });
      
      if (isArrayDiff || hasDiffMarkers) {
        if (selectedFields && selectedFields.size > 0) {
          // Direct check: if the field name matches, or if shouldShowField says it matches
          if (selectedFields.has(key) || selectedFields.has(newPath) || shouldShowField(newPath, selectedFields)) {
            // We have changes in this array field
            changes.push({ path: newPath, oldValue: "[Array changes]", newValue: "[Array changes]" });
          }
        } else {
          changes.push({ path: newPath, oldValue: "[Array changes]", newValue: "[Array changes]" });
        }
        return;
      }
    }

    // Generic jsondiffpatch array delta (e.g. ammo.traits): { _t: "a", _2: [value, 0, 0], ... }
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      (value as Record<string, unknown>)._t === "a"
    ) {
      const expanded = expandJsonDiffpatchArrayDeltaToChanges(
        value as Record<string, unknown>,
        newPath
      );
      for (const ec of expanded) {
        if (shouldOmitField(ec.path)) {
          continue;
        }
        if (
          selectedFields &&
          selectedFields.size > 0 &&
          !shouldShowField(ec.path, selectedFields)
        ) {
          continue;
        }
        changes.push(ec);
      }
      return;
    }
    
    // Skip fields that aren't in selectedFields (if filtering is active)
    // But first check if any selected field pattern could match paths starting with the current path
    // This is needed for nested paths like "weapons.*.ammo.aimingTime"
    let shouldRecurseForNested = false;
    if (selectedFields && selectedFields.size > 0 && value && typeof value === "object" && !Array.isArray(value)) {
      const normalizedPath = newPath.replace(/\[\d+\]/g, ".*").replace(/\.\d+(?=\.|$)/g, ".*");
      
      for (const selectedField of selectedFields) {
        const normalizedSelected = selectedField.replace(/\[\d+\]/g, ".*").replace(/\.\d+(?=\.|$)/g, ".*");
        // Check if selected field starts with current path (reverse prefix match)
        // e.g., "weapons.*.ammo.aimingTime" starts with "weapons"
        if (normalizedSelected.startsWith(normalizedPath + ".") || 
            normalizedSelected.startsWith(normalizedPath + "[")) {
          shouldRecurseForNested = true;
          break;
        }
        // Also check segment-by-segment if selected field could match nested paths
        const pathSegments = normalizedPath.split(".");
        const selectedSegments = normalizedSelected.split(".");
        if (pathSegments.length <= selectedSegments.length) {
          let matches = true;
          for (let i = 0; i < pathSegments.length; i++) {
            const pathSeg = pathSegments[i];
            const selectedSeg = selectedSegments[i];
            if (!pathSeg || !selectedSeg) {
              matches = false;
              break;
            }
            if (selectedSeg === "*" || selectedSeg === ".*") {
              continue;
            }
            const pathSegment = pathSeg.replace(/\.\*/g, "");
            const selectedSegment = selectedSeg.replace(/\.\*/g, "");
            if (pathSegment !== selectedSegment) {
              matches = false;
              break;
            }
          }
          if (matches) {
            shouldRecurseForNested = true;
            break;
          }
        }
      }
    }
    
    if (selectedFields && selectedFields.size > 0) {
      const isNumericKey = /^\d+$/.test(key);
      // For numeric keys, check if the parent path matches (e.g., "specialities.0" -> check "specialities")
      const parentMatches = isNumericKey && path && shouldShowField(path, selectedFields);
      // Also check if the field name itself matches (e.g., "specialities" matches "specialities")
      // This handles the case where specialities is stored as an object with numeric keys
      const fieldNameMatches = path === "" && shouldShowField(key, selectedFields);
      const shouldShow = shouldShowField(newPath, selectedFields) || parentMatches || fieldNameMatches || shouldRecurseForNested;
      
      // If we should recurse for nested paths, find nested changes
      // For example, if "weapons.*.ammo.aimingTime" is selected and we're at "weapons" or "weapons.0",
      // we need to recurse to find "weapons.0.ammo.aimingTime"
      if (shouldRecurseForNested && value && typeof value === "object" && !Array.isArray(value)) {
        // Selected field pattern could match nested paths, so recurse to find matching fields
        changes.push(...findAllChanges(value, newPath, selectedFields));
        return;
      }
      
      // Also handle numeric keys with parent matches
      if ((parentMatches) && value && typeof value === "object" && !Array.isArray(value)) {
        changes.push(...findAllChanges(value, newPath, selectedFields));
        return;
      }
      
      if (!shouldShow) {
        return;
      }
    }
    
    const diffValues = extractDiffValues(value);
    if (diffValues) {
      changes.push({ path: newPath, oldValue: diffValues.oldValue, newValue: diffValues.newValue });
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      // Recursively check nested objects
      changes.push(...findAllChanges(value, newPath, selectedFields));
    } else if (Array.isArray(value) && value.length > 0 && typeof value[0] !== "string") {
      // Recursively check array elements that aren't diff markers
      value.forEach((item, index) => {
        const arrayItemPath = `${newPath}[${index}]`;
        // Skip omitted fields and filtered fields for array items too
        if (!shouldOmitField(arrayItemPath) && 
            (!selectedFields || selectedFields.size === 0 || shouldShowField(arrayItemPath, selectedFields))) {
          changes.push(...findAllChanges(item, arrayItemPath, selectedFields));
        }
      });
    }
  });

  return changes;
}

/**
 * Render array changes (specialities, weapons, etc.)
 */
function renderArrayChanges(
  fieldName: string,
  diffValue: any,
  getDisplayName?: (item: any) => string
) {
  const changes: ReactElement[] = [];

  if (Array.isArray(diffValue)) {
    diffValue.forEach((element, index) => {
      if (isArrayDiffAdded(element)) {
        const item = element[1];
        const displayName = getDisplayName ? getDisplayName(item) : formatValue(item);
        changes.push(
          <div key={`added-${index}`} className="flex items-center gap-2 text-sm min-w-0">
            <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300 flex-shrink-0">
              + Added
            </Badge>
            <span className="break-words min-w-0">{fieldName}: {displayName}</span>
          </div>
        );
      } else if (isArrayDiffRemoved(element)) {
        const item = element[1];
        const displayName = getDisplayName ? getDisplayName(item) : formatValue(item);
        changes.push(
          <div key={`removed-${index}`} className="flex items-center gap-2 text-sm min-w-0">
            <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300 flex-shrink-0">
              - Removed
            </Badge>
            <span className="break-words min-w-0">{fieldName}: {displayName}</span>
          </div>
        );
      } else if (isArrayDiffModified(element)) {
        const item = element[1];
        if (isDiff(item)) {
          changes.push(
            <div key={`modified-${index}`} className="text-sm">
              <div className="mb-1 font-medium text-muted-foreground">{fieldName} modified</div>
              <PatchFieldChangesTable>
                {renderFieldChangeRow(
                  "",
                  item.__old,
                  item.__new,
                  `array-mod-${fieldName}-${index}`
                )}
              </PatchFieldChangesTable>
            </div>
          );
        }
      }
    });
  } else if (isDiff(diffValue)) {
    changes.push(
      <div key="diff" className="text-sm">
        <PatchFieldChangesTable>
          {renderFieldChangeRow(fieldName, diffValue.__old, diffValue.__new, `${fieldName}-diff`)}
        </PatchFieldChangesTable>
      </div>
    );
  }

  return changes.length > 0 ? <div className="space-y-1">{changes}</div> : null;
}

export function UnitChangeCard({ unit, oldUnit, diff, changeType, divisionsMap, selectedFields }: UnitChangeCardProps) {
  // Early return: Hide units with no matching changes when filtering is active
  if (changeType === "changed" && selectedFields && selectedFields.size > 0 && diff) {
    // Check if there are any visible changes after filtering
    const allChanges = findAllChanges(diff, "", selectedFields);
    const hasVisibleChanges = allChanges.length > 0;
    
    if (!hasVisibleChanges) {
      return null;
    }
  }

  const unitImagePath = `/images/units/${getUnitImageFilename(unit.descriptorName)}`;

  return (
    <Card className="border-border/70 gap-1">
      <CardHeader className="px-3 pt-3 pb-0 sm:px-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <img
              src={unitImagePath}
              alt={unit.name}
              className="h-20 w-40 object-cover rounded border"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <CardTitle className="text-lg font-semibold break-words">{unit.name}</CardTitle>
            {/* Division Icons */}
            {unit.divisions && unit.divisions.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {unit.divisions.slice(0, 8).map((divisionDescriptor) => {
                  const division = divisionsMap?.get(divisionDescriptor);
                  return (
                    <Tooltip key={divisionDescriptor}>
                      <TooltipTrigger asChild>
                        <div className="w-6 h-6 cursor-help flex-shrink-0">
                          <img
                            src={getDivisionImagePath(divisionDescriptor)}
                            alt={division?.name || divisionDescriptor}
                            className="w-full h-full object-cover rounded border"
                            title={division?.name || divisionDescriptor}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{division?.name || divisionDescriptor}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
                {unit.divisions.length > 8 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-muted-foreground cursor-help">
                        +{unit.divisions.length - 8} more
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="space-y-1">
                        {unit.divisions.slice(8).map((divisionDescriptor) => {
                          const division = divisionsMap?.get(divisionDescriptor);
                          return (
                            <div key={divisionDescriptor}>
                              {division?.name || divisionDescriptor}
                            </div>
                          );
                        })}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            )}
            <div className="mt-2">
              <Badge
                variant={
                  changeType === "added"
                    ? "default"
                    : changeType === "removed"
                    ? "destructive"
                    : "secondary"
                }
              >
                {changeType === "added"
                  ? "New Unit"
                  : changeType === "removed"
                  ? "Removed Unit"
                  : "Changed Unit"}
              </Badge>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-3 pb-3 pt-1 sm:px-4">
        <div className="space-y-3 min-w-0">
          {changeType === "removed" && (
            <div className="text-sm text-muted-foreground break-words">
              This unit was removed from the game.
            </div>
          )}

          {changeType === "changed" && diff && (
            <div className="space-y-3">
              {/* Debug: Track if we rendered any changes */}
              {(() => {
                let hasRenderedChanges = false;
                const renderedElements: ReactElement[] = [];
                const scalarFieldRows: ReactElement[] = [];
                
                // Render all field changes
                Object.entries(diff).forEach(([fieldName, fieldDiff]) => {
                // Skip if it's an empty object or null
                if (!fieldDiff || (typeof fieldDiff === "object" && Object.keys(fieldDiff).length === 0)) {
                  return null;
                }

                // Handle specialities array
                if (fieldName === "specialities") {
                  // Skip if field is not in selectedFields (if filtering is active)
                  if (selectedFields && selectedFields.size > 0 && !shouldShowField(fieldName, selectedFields)) {
                    return;
                  }
                  
                  const specialityChanges: ReactElement[] = [];
                  
                  // Handle both array diff format and object with numeric keys
                  if (Array.isArray(fieldDiff)) {
                    fieldDiff.forEach((specialityDiffElement: any, index: number) => {
                      if (isArrayDiffAdded(specialityDiffElement)) {
                        const speciality = specialityDiffElement[1];
                        specialityChanges.push(
                          <div key={`speciality-added-${index}`} className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300 flex-shrink-0">
                              + Added
                            </Badge>
                            <TraitIcon speciality={speciality} size="md" className="flex-shrink-0" />
                            <span className="text-sm break-words min-w-0">{speciality}</span>
                          </div>
                        );
                      } else if (isArrayDiffRemoved(specialityDiffElement)) {
                        const speciality = specialityDiffElement[1];
                        specialityChanges.push(
                          <div key={`speciality-removed-${index}`} className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300 flex-shrink-0">
                              - Removed
                            </Badge>
                            <TraitIcon speciality={speciality} size="md" className="flex-shrink-0" />
                            <span className="text-sm break-words min-w-0">{speciality}</span>
                          </div>
                        );
                      }
                    });
                  } else if (fieldDiff && typeof fieldDiff === "object" && !Array.isArray(fieldDiff)) {
                    // Object with numeric keys format - extract added/removed by comparing old and new
                    const oldSpecialities = oldUnit?.specialities || [];
                    const newSpecialities = unit.specialities || [];
                    const oldSet = new Set(oldSpecialities);
                    const newSet = new Set(newSpecialities);
                    
                    newSpecialities.forEach((speciality) => {
                      if (!oldSet.has(speciality)) {
                        specialityChanges.push(
                          <div key={`speciality-added-${speciality}`} className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300 flex-shrink-0">
                              + Added
                            </Badge>
                            <TraitIcon speciality={speciality} size="md" className="flex-shrink-0" />
                            <span className="text-sm break-words min-w-0">{speciality}</span>
                          </div>
                        );
                      }
                    });
                    
                    oldSpecialities.forEach((speciality) => {
                      if (!newSet.has(speciality)) {
                        specialityChanges.push(
                          <div key={`speciality-removed-${speciality}`} className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300 flex-shrink-0">
                              - Removed
                            </Badge>
                            <TraitIcon speciality={speciality} size="md" className="flex-shrink-0" />
                            <span className="text-sm break-words min-w-0">{speciality}</span>
                          </div>
                        );
                      }
                    });
                  }
                  
                  if (specialityChanges.length > 0) {
                    hasRenderedChanges = true;
                    renderedElements.push(
                      <div key={fieldName}>
                        <div className="font-medium text-sm mb-2">Traits:</div>
                        <div className="space-y-1">{specialityChanges}</div>
                      </div>
                    );
                  }
                  return;
                }

                // Handle divisions array
                if (fieldName === "divisions") {
                  // Skip if field is not in selectedFields (if filtering is active)
                  if (selectedFields && selectedFields.size > 0 && !shouldShowField(fieldName, selectedFields)) {
                    return;
                  }
                  
                  const divisionChanges: ReactElement[] = [];
                  
                  // Handle both array diff format and object with numeric keys
                  if (Array.isArray(fieldDiff)) {
                    fieldDiff.forEach((divisionDiffElement: any, index: number) => {
                      if (isArrayDiffAdded(divisionDiffElement)) {
                        const divisionDescriptor = divisionDiffElement[1];
                        const division = divisionsMap?.get(divisionDescriptor);
                        divisionChanges.push(
                          <div key={`division-added-${index}`} className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300 flex-shrink-0">
                              + Added
                            </Badge>
                            <div className="flex-shrink-0">
                              <img
                                src={getDivisionImagePath(divisionDescriptor)}
                                alt={division?.name || divisionDescriptor}
                                className="h-8 w-8 object-cover rounded border flex-shrink-0"
                                title={division?.name || divisionDescriptor}
                              />
                            </div>
                            <span className="text-sm break-words min-w-0">{division?.name || "Unknown Division"}</span>
                          </div>
                        );
                      } else if (isArrayDiffRemoved(divisionDiffElement)) {
                        const divisionDescriptor = divisionDiffElement[1];
                        const division = divisionsMap?.get(divisionDescriptor);
                        divisionChanges.push(
                          <div key={`division-removed-${index}`} className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300 flex-shrink-0">
                              - Removed
                            </Badge>
                            <div className="flex-shrink-0">
                              <img
                                src={getDivisionImagePath(divisionDescriptor)}
                                alt={division?.name || divisionDescriptor}
                                className="h-8 w-8 object-cover rounded border flex-shrink-0"
                                title={division?.name || divisionDescriptor}
                              />
                            </div>
                            <span className="text-sm break-words min-w-0">{division?.name || "Unknown Division"}</span>
                          </div>
                        );
                      }
                    });
                  } else if (fieldDiff && typeof fieldDiff === "object" && !Array.isArray(fieldDiff)) {
                    // Object with numeric keys format - extract added/removed by comparing old and new
                    const oldDivisions = oldUnit?.divisions || [];
                    const newDivisions = unit.divisions || [];
                    const oldSet = new Set(oldDivisions);
                    const newSet = new Set(newDivisions);
                    
                    newDivisions.forEach((divisionDescriptor) => {
                      if (!oldSet.has(divisionDescriptor)) {
                        const division = divisionsMap?.get(divisionDescriptor);
                        divisionChanges.push(
                          <div key={`division-added-${divisionDescriptor}`} className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300 flex-shrink-0">
                              + Added
                            </Badge>
                            <div className="flex-shrink-0">
                              <img
                                src={getDivisionImagePath(divisionDescriptor)}
                                alt={division?.name || divisionDescriptor}
                                className="h-8 w-8 object-cover rounded border flex-shrink-0"
                                title={division?.name || divisionDescriptor}
                              />
                            </div>
                            <span className="text-sm break-words min-w-0">{division?.name || "Unknown Division"}</span>
                          </div>
                        );
                      }
                    });
                    
                    oldDivisions.forEach((divisionDescriptor) => {
                      if (!newSet.has(divisionDescriptor)) {
                        const division = divisionsMap?.get(divisionDescriptor);
                        divisionChanges.push(
                          <div key={`division-removed-${divisionDescriptor}`} className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300 flex-shrink-0">
                              - Removed
                            </Badge>
                            <div className="flex-shrink-0">
                              <img
                                src={getDivisionImagePath(divisionDescriptor)}
                                alt={division?.name || divisionDescriptor}
                                className="h-8 w-8 object-cover rounded border flex-shrink-0"
                                title={division?.name || divisionDescriptor}
                              />
                            </div>
                            <span className="text-sm break-words min-w-0">{division?.name || "Unknown Division"}</span>
                          </div>
                        );
                      }
                    });
                  }
                  
                  if (divisionChanges.length > 0) {
                    hasRenderedChanges = true;
                    renderedElements.push(
                      <div key={fieldName}>
                        <div className="font-medium text-sm mb-2">Divisions:</div>
                        <div className="space-y-1">{divisionChanges}</div>
                      </div>
                    );
                  }
                  return;
                }

                // Handle weapons array
                if (fieldName === "weapons") {
                  // When filtering is active, only render the weapons section if
                  // the user selected at least one weapon-related field (or a descendant).
                  if (selectedFields && selectedFields.size > 0 && !shouldShowField("weapons", selectedFields)) {
                    return;
                  }

                  const weaponChanges: ReactElement[] = [];
                  
                  // jsondiffpatch can represent array diffs in two ways:
                  // 1. As an array of diff markers: ['+', value], ['-', value], ['~', index, diff]
                  // 2. As an object with numeric keys: { 0: diff, 1: diff } (when arrays are sorted)
                  
                  if (Array.isArray(fieldDiff)) {
                    // Format 1: Array of diff markers
                    fieldDiff.forEach((weaponDiffElement: any, index: number) => {
                      if (isArrayDiffAdded(weaponDiffElement)) {
                        const weapon = weaponDiffElement[1];
                        const weaponName = weapon?.ammo?.name || `Weapon ${index + 1}`;
                        weaponChanges.push(
                          <div key={`weapon-added-${index}`} className="mb-3 space-y-1.5">
                            <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
                              + Added Weapon
                            </Badge>
                            <div className="text-sm font-medium break-words">{weaponName}</div>
                          </div>
                        );
                      } else if (isArrayDiffRemoved(weaponDiffElement)) {
                        const weapon = weaponDiffElement[1];
                        const weaponName = weapon?.ammo?.name || `Weapon ${index + 1}`;
                        weaponChanges.push(
                          <div key={`weapon-removed-${index}`} className="mb-3 space-y-1.5">
                            <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300">
                              - Removed Weapon
                            </Badge>
                            <div className="text-sm font-medium break-words">{weaponName}</div>
                          </div>
                        );
                      } else if (isArrayDiffModified(weaponDiffElement)) {
                        // ['~', index, diff] format (3 elements)
                        const weaponIndex = weaponDiffElement[1];
                        const weaponDiff = weaponDiffElement.length >= 3 ? weaponDiffElement[2] : weaponDiffElement[1];
                        if (weaponDiff && typeof weaponDiff === "object") {
                          const nestedChanges = findAllChanges(weaponDiff, `weapons[${weaponIndex}]`, selectedFields);
                          // Filter out omitted fields and fields not in selectedFields
                          const filteredChanges = nestedChanges.filter(change => 
                            !shouldOmitField(change.path) &&
                            (!selectedFields || selectedFields.size === 0 || shouldShowField(change.path, selectedFields))
                          );
                          if (filteredChanges.length > 0) {
                            // Try to get weapon name from the new unit
                            const weapon = unit.weapons?.[weaponIndex];
                            const weaponName = weapon?.ammo?.name || weapon?.ammo?.descriptorName || `Weapon ${weaponIndex + 1}`;
                            weaponChanges.push(
                              <div key={`weapon-modified-${weaponIndex}`} className="mb-3 space-y-1.5">
                                <div className="text-sm font-medium">{weaponName}</div>
                                <PatchFieldChangesTable>
                                  {filteredChanges.map((change) =>
                                    renderFieldChangeRow(
                                      change.path,
                                      change.oldValue,
                                      change.newValue,
                                      change.path
                                    )
                                  )}
                                </PatchFieldChangesTable>
                              </div>
                            );
                          }
                        }
                      }
                    });
                  } else if (fieldDiff && typeof fieldDiff === "object" && !Array.isArray(fieldDiff)) {
                    // Format 2: Object with numeric keys representing array indices
                    Object.entries(fieldDiff).forEach(([weaponIndexStr, weaponDiff]) => {
                      const weaponIndex = parseInt(weaponIndexStr, 10);
                      if (!isNaN(weaponIndex) && weaponDiff && typeof weaponDiff === "object") {
                        const nestedChanges = findAllChanges(weaponDiff, `weapons[${weaponIndex}]`, selectedFields);
                        // Filter out omitted fields and fields not in selectedFields
                        const filteredChanges = nestedChanges.filter(change => 
                          !shouldOmitField(change.path) &&
                          (!selectedFields || selectedFields.size === 0 || shouldShowField(change.path, selectedFields))
                        );
                        if (filteredChanges.length > 0) {
                          // Try to get weapon name from the new unit
                          const weapon = unit.weapons?.[weaponIndex];
                          const weaponName = weapon?.ammo?.name || weapon?.ammo?.descriptorName || `Weapon ${weaponIndex + 1}`;
                          weaponChanges.push(
                            <div key={`weapon-modified-${weaponIndex}`} className="mb-3 space-y-1.5">
                              <div className="text-sm font-medium">{weaponName}</div>
                              <PatchFieldChangesTable>
                                {filteredChanges.map((change) =>
                                  renderFieldChangeRow(
                                    change.path,
                                    change.oldValue,
                                    change.newValue,
                                    change.path
                                  )
                                )}
                              </PatchFieldChangesTable>
                            </div>
                          );
                        }
                      }
                    });
                  }

                  if (weaponChanges.length > 0) {
                    hasRenderedChanges = true;
                    renderedElements.push(
                      <div key={fieldName} className="min-w-0">
                        <div className="mb-1.5 text-sm font-medium">Weapon changes</div>
                        {weaponChanges}
                      </div>
                    );
                  }
                  return;
                }

                // Handle regular field changes (jsondiffpatch format: [oldValue, newValue])
                const diffValues = extractDiffValues(fieldDiff);
                if (diffValues) {
                  // Skip omitted fields
                  if (shouldOmitField(fieldName)) {
                    return;
                  }
                  // Skip fields that aren't in selectedFields (if filtering is active)
                  if (selectedFields && selectedFields.size > 0 && !shouldShowField(fieldName, selectedFields)) {
                    return;
                  }
                  // Only show if values are actually different
                  if (JSON.stringify(diffValues.oldValue) !== JSON.stringify(diffValues.newValue)) {
                    hasRenderedChanges = true;
                    scalarFieldRows.push(
                      renderFieldChangeRow(
                        fieldName,
                        diffValues.oldValue,
                        diffValues.newValue,
                        fieldName
                      )
                    );
                  }
                  return;
                }

                // Handle nested object changes
                if (fieldDiff && typeof fieldDiff === "object" && !Array.isArray(fieldDiff)) {
                  const nestedChanges: ReactElement[] = [];
                  Object.entries(fieldDiff).forEach(([nestedField, nestedDiff]) => {
                    const nestedPath = `${fieldName}.${nestedField}`;
                    // Skip omitted fields
                    if (shouldOmitField(nestedPath)) {
                      return;
                    }
                    // Skip fields that aren't in selectedFields (if filtering is active)
                    if (selectedFields && selectedFields.size > 0 && !shouldShowField(nestedPath, selectedFields)) {
                      return;
                    }
                    
                    const nestedDiffValues = extractDiffValues(nestedDiff);
                    if (nestedDiffValues) {
                      nestedChanges.push(
                        renderFieldChangeRow(
                          nestedPath,
                          nestedDiffValues.oldValue,
                          nestedDiffValues.newValue,
                          nestedPath
                        )
                      );
                    } else if (nestedDiff && typeof nestedDiff === "object") {
                      // Recursively handle deeper nesting
                      const deeperChanges = findAllChanges(nestedDiff, nestedPath, selectedFields);
                      deeperChanges.forEach((change) => {
                        // Double-check the full path isn't omitted and is in selectedFields
                        if (
                          !shouldOmitField(change.path) &&
                          (!selectedFields ||
                            selectedFields.size === 0 ||
                            shouldShowField(change.path, selectedFields))
                        ) {
                          nestedChanges.push(
                            renderFieldChangeRow(
                              change.path,
                              change.oldValue,
                              change.newValue,
                              change.path
                            )
                          );
                        }
                      });
                    }
                  });

                  if (nestedChanges.length > 0) {
                    hasRenderedChanges = true;
                    renderedElements.push(
                      <div key={fieldName}>
                        <div className="mb-2 font-medium text-sm">{formatFieldName(fieldName)}</div>
                        <PatchFieldChangesTable>{nestedChanges}</PatchFieldChangesTable>
                      </div>
                    );
                  }
                }
              });

              // Render all collected elements
              if (scalarFieldRows.length > 0) {
                renderedElements.unshift(
                  <div key="scalar-field-changes" className="min-w-0">
                    <PatchFieldChangesTable>{scalarFieldRows}</PatchFieldChangesTable>
                  </div>
                );
              }
              const allRendered = [...renderedElements];

              // Fallback: use recursive search to catch any missed changes
              const allChanges = findAllChanges(diff, "", selectedFields);
              // Filter out changes that were already shown explicitly and omitted fields
              const explicitFields = new Set(Object.keys(diff));
              const missedChanges = allChanges.filter(
                (change) => {
                  // Skip omitted fields
                  if (shouldOmitField(change.path)) {
                    return false;
                  }
                  // Skip fields that aren't in selectedFields (if filtering is active)
                  if (selectedFields && selectedFields.size > 0 && !shouldShowField(change.path, selectedFields)) {
                    return false;
                  }
                  const firstPart = change.path.split(".")[0];
                  if (!firstPart) return false;
                  // Direct top-level key (e.g. commandPoints)
                  if (explicitFields.has(firstPart)) return false;
                  // Nested paths use "weapons[0].ammo.heDamage" but diff keys are "weapons".
                  // Treat first segment as indexed root: weapons[0] -> weapons
                  const stripIndex = firstPart.replace(/\[\d+]$/, "");
                  if (stripIndex !== firstPart && explicitFields.has(stripIndex)) {
                    return false;
                  }
                  return true;
                }
              );
              
              if (missedChanges.length > 0) {
                hasRenderedChanges = true;
                allRendered.push(
                  <div key="fallback-field-changes" className="min-w-0">
                    <PatchFieldChangesTable>
                      {missedChanges.map((change) =>
                        renderFieldChangeRow(
                          change.path,
                          change.oldValue,
                          change.newValue,
                          change.path
                        )
                      )}
                    </PatchFieldChangesTable>
                  </div>
                );
              }

              // If no changes were rendered, show debug info
              if (!hasRenderedChanges && Object.keys(diff).length > 0) {
                console.warn("Unit marked as changed but no changes rendered:", {
                  unit: unit.descriptorName,
                  diff: diff,
                  diffKeys: Object.keys(diff),
                });
                
                return (
                  <>
                    <div className="text-sm text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 p-2 rounded border border-yellow-300 dark:border-yellow-700">
                      <div className="font-medium mb-1">⚠️ Debug: Unit marked as changed but no visible changes detected</div>
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-muted-foreground">Show raw diff data</summary>
                        <pre className="mt-2 text-xs overflow-auto max-h-48 bg-muted p-2 rounded">
                          {JSON.stringify(diff, null, 2)}
                        </pre>
                      </details>
                    </div>
                  </>
                );
              }

              return allRendered.length > 0 ? (
                <>
                  {allRendered}
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No visible changes detected (may be internal structure changes).
                </div>
              );
              })()}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

